/**
 * 需要想定 daily チャートの高さをスクロール可視高とカード内 chrome から動的に決める hook。
 *
 * design/demand/forecast-today.html の `renderChart` 高さ計算を React 側に移植:
 * - 2 カラム (data-dft-workspace が `display: grid`) ではスクロール可視高から
 *   チャート上の chrome (凡例・気象帯等) を除いた高さいっぱいを使う。
 * - stacked (1 カラム) ではその高さの 55%・上限 720px に抑え、下の 48 コマ表が
 *   必ず一部見えるようにする。
 * - いずれも最低 380px を保つ。
 */
import { useLayoutEffect, useState } from 'react'
import type { RefObject } from 'react'

const MIN_CHART_HEIGHT = 380
const STACKED_HEIGHT_CAP = 720
const STACKED_HEIGHT_RATIO = 0.55

/**
 * チャート上の chrome (凡例・気象帯など) を除いた「カード内でチャートに割ける高さ」を
 * 決める純関数。
 *
 * `scrollClientHeight` はスクロールコンテナの可視高、`aboveChartHeight` はチャート
 * カード上端からチャート上端までの高さ (= チャートより上に積まれた凡例・気象帯等の
 * chrome の合計高)。
 *
 * **チャートの絶対位置ではなくカード内オフセットで測る理由 (issue #6446 の発散対策)**:
 * 旧実装は可視領域を `scrollContainer.clientHeight - (チャート上端 - スクロール上端)`
 * で算出し、かつチャートカード自身も ResizeObserver で観測していたため、「高さ設定 →
 * カードが伸びる → 再測定」の自己参照ループを持っていた。チャートカードは 2 カラム時
 * `position: sticky; top: 0`。チャートが伸びてカードが可視領域より高くなると sticky の
 * 「要素が容器より高い時は下端で pin」挙動になり、カード下端が固定されてチャート上端が
 * 上へ動く。すると「チャート上端 - スクロール上端」がチャート高に連動して縮み、
 * `available` が増える → さらに伸びる、で上方向へ発散する (実測: 下端固定のまま
 * 15px/frame で伸び続けた)。
 *
 * 移植元 design の `_dftAvailChartAreaH()` はチャートの位置を見ず、レイアウト上の
 * chrome 高を `main.clientHeight` から引くだけ (= 位置に依存しない)。それに合わせ、
 * チャートの絶対位置ではなく**カード内のチャート上オフセット** (`aboveChartHeight` =
 * `チャート上端 - カード上端`) から算出する。この差分は sticky でカードごと移動しても
 * 一定なので、測定の基準が自分の出力 (伸びたチャート高) に影響されず不動点へ即収束する。
 */
export function forecastChartHeightFor({
  scrollClientHeight,
  aboveChartHeight,
  isTwoCol,
}: {
  scrollClientHeight: number
  aboveChartHeight: number
  isTwoCol: boolean
}): number {
  const available = scrollClientHeight - Math.max(0, aboveChartHeight)
  let target = available
  if (!isTwoCol) {
    target = Math.min(
      Math.round(target * STACKED_HEIGHT_RATIO),
      STACKED_HEIGHT_CAP,
    )
  }
  return Math.max(MIN_CHART_HEIGHT, target)
}

function findScrollContainer(el: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = el
  while (node !== null) {
    const style = window.getComputedStyle(node)
    if (
      style.overflowY === 'auto' ||
      style.overflowY === 'scroll' ||
      style.overflowY === 'hidden'
    ) {
      return node
    }
    node = node.parentElement
  }
  return null
}

function findWorkspaceRoot(el: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = el
  while (node !== null) {
    if (node.dataset.dftWorkspace !== undefined) {
      return node
    }
    node = node.parentElement
  }
  return null
}

export function useForecastChartHeight(
  containerRef: RefObject<HTMLElement | null>,
  /**
   * チャートより**上**にあるカード内 chrome の構成が変わった時に値が変わる key。
   *
   * カード自身は意図的に観測しない (下の ResizeObserver のコメント参照、issue
   * #6446 の自己参照ループ回避) ため、チャート上の要素が出入りしても再測定が
   * 走らず、その分だけカードが可視領域からはみ出す。カードを観測せずに再測定
   * したいので、**出入りする側が明示的に key を変える**形にする。
   *
   * 例: 実績トレンドの帯 (凡例の直下) は非同期の PreviewTrendReflection 応答や
   * 集計窓 W の変更で出入りする。
   */
  aboveChartRevision: string | number | boolean = 0,
): number {
  const [chartHeight, setChartHeight] = useState(MIN_CHART_HEIGHT)

  useLayoutEffect(() => {
    const container = containerRef.current
    if (container === null || typeof window === 'undefined') return

    const scrollContainer = findScrollContainer(container)
    const workspaceRoot = findWorkspaceRoot(container)
    if (scrollContainer === null) return

    // aboveChartHeight (チャート上 chrome 高) の測定にカード上端を使う。
    // container の parent は ForecastCard の Stack、さらに上がチャートカード Box。
    const chartCard = container.parentElement?.parentElement ?? null

    const measure = (): void => {
      const containerRect = container.getBoundingClientRect()
      const isTwoCol =
        workspaceRoot !== null &&
        window.getComputedStyle(workspaceRoot).display === 'grid'
      // チャート上端をカード上端からの相対で測る (issue #6446: sticky でカードごと
      // 移動しても差分は一定。絶対位置で測るとチャート高に連動して発散する。純関数の
      // JSDoc 参照)。カード未解決時は 0 (= chrome 無しとみなす) にフォールバック。
      const aboveChartHeight =
        chartCard !== null
          ? containerRect.top - chartCard.getBoundingClientRect().top
          : 0
      const next = forecastChartHeightFor({
        scrollClientHeight: scrollContainer.clientHeight,
        aboveChartHeight,
        isTwoCol,
      })
      setChartHeight((prev) => (prev === next ? prev : next))
    }

    measure()

    let resizeTimer: ReturnType<typeof setTimeout> | undefined
    const handleResize = (): void => {
      clearTimeout(resizeTimer)
      resizeTimer = setTimeout(measure, 150)
    }
    window.addEventListener('resize', handleResize)

    // スクロールコンテナ (viewport 由来の可視高) のみ観測する。チャートカード自身は
    // 観測しない (issue #6446): カードを観測すると (1) 自身の高さ設定でカードが変わり
    // 再測定 → 自己参照ループの起点になる、(2) 確定操作で card 内 chrome (気象観測地点の
    // 一時切替 UI) が縮んだだけで再測定が走りチャートが動く。カードを観測しなければ、
    // 確定は scrollContainer の box を変えない (可視高・幅とも不変) ので再測定が発火せず
    // チャート高は据え置かれる。可視高が実際に変わる window/レイアウト変化 (2 カラム
    // 判定 = 幅条件を含む) は scrollContainer の resize と window resize で拾える。
    let resizeObserver: ResizeObserver | undefined
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(handleResize)
      resizeObserver.observe(scrollContainer)
    }

    return () => {
      window.removeEventListener('resize', handleResize)
      clearTimeout(resizeTimer)
      if (resizeObserver !== undefined) {
        resizeObserver.disconnect()
      }
    }
  }, [containerRef, aboveChartRevision])

  return chartHeight
}
